import { useState, useLayoutEffect, RefObject } from 'react';

import { ResizeObserver } from '@juggle/resize-observer';

type Bounds = {
  width: number;
  height: number;
};

/**
 * Gets the measure of an element using `ResizeObserver`
 * @param ref Element to be measured
 * @returns an object containing `width` and `height` for the element passed as ref (without padding)
 * @see AnimateHeight Tabs WorkflowBar
 */
export default function useMeasure(ref: RefObject<Element | null>) {
  const [bounds, setBounds] = useState<Bounds>({
    width: 0,
    height: 0,
  });

  useLayoutEffect(() => {
    if (!ref?.current) return undefined;

    function onResize([entry]: ResizeObserverEntry[]) {
      setBounds({
        height: entry.contentRect.height,
        width: entry.contentRect.width,
      });
    }

    const observer = new ResizeObserver(onResize);
    observer.observe(ref.current);

    return () => {
      observer.disconnect();
    };

    // while it's true that having ref.current as a dependency can be incorrect as
    // it is mutable, in this case it is more correct than just ref, because it covers the case
    // where the ref is not initially on a DOM element, and then it is set to one, because
    // current goes from undefined to a some value
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref?.current]);

  return bounds;
}
